/**
 * OwnInsightsStartCard — turns own insights (result tailoring) on for an investor's due-diligence company from the
 * chat (PLT-7869).
 *
 * The chat-only tool `request_own_insights_start` checks that the user may turn own insights on and echoes the
 * company back. Turn on POSTs to the same route as the company page's "Add own insights" button, waits for the
 * tailored version to be computed (`useStartOwnInsights`, which also refreshes a company page open beside the chat),
 * and then re-enters the agent by appending a user turn via `runtime.thread.append`, as `TailoringSelectionCard`
 * does, so the agent goes on to its proposal. Not now appends a turn too, so the agent can offer what it still can.
 * A failed computation appends nothing: the agent would otherwise propose on a version without results.
 *
 * Reuses the `tailoring-batch-card-*` classes: this card is the first step of the tailoring flow and reads as part
 * of it.
 */
import { useAssistantRuntime } from '@assistant-ui/react';
import { useStartOwnInsights } from '@upright/ui/common/upright/model/output/scores/csrd/csrd-company-double-materiality/result-tailoring/use-start-own-insights';
import { useEffect, useState } from 'react';
import { composerState } from './upright-chat-shared';

export interface OwnInsightsStartCardProps {
  companyId: string;
  companyName: string;
}

export function OwnInsightsStartCard({ companyId, companyName }: OwnInsightsStartCardProps) {
  const runtime = useAssistantRuntime();
  const [isDeclined, setIsDeclined] = useState(false);

  const { startMutation, started, versionState } = useStartOwnInsights(companyId);
  const isReady = versionState === 'ready';

  const continueWith = (prompt: string) => {
    // Tagged like the follow-up chips, as `TailoringSelectionCard` does: a UI-driven re-entry.
    composerState.pendingSource = 'followup_suggestion';
    runtime.thread.append(prompt);
  };

  useEffect(() => {
    if (!isReady) return;
    continueWith(`Own insights are now on for ${companyName}. Continue with the proposal.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  if (isDeclined) {
    return (
      <section className="tailoring-batch-card tailoring-batch-card--receipt" aria-label="Own insights not turned on">
        <header className="tailoring-batch-card-header">
          <div className="tailoring-batch-card-title">Own insights not turned on</div>
          <div className="tailoring-batch-card-subtitle">{`${companyName} keeps Upright's assessment only.`}</div>
        </header>
      </section>
    );
  }

  if (versionState === 'failed') {
    return (
      <section className="tailoring-batch-card tailoring-batch-card--receipt" aria-label="Own insights">
        <header className="tailoring-batch-card-header">
          <div className="tailoring-batch-card-title">{`Own insights could not be turned on for ${companyName}`}</div>
        </header>
        <div className="tailoring-batch-card-error" role="alert">
          The tailored version could not be computed. Contact Upright support.
        </div>
      </section>
    );
  }

  if (started) {
    return (
      <section className="tailoring-batch-card tailoring-batch-card--receipt" aria-label="Own insights">
        <header className="tailoring-batch-card-header">
          <div className="tailoring-batch-card-title">
            {isReady ? `Own insights are on for ${companyName}` : `Turning on own insights for ${companyName}`}
          </div>
          <div className="tailoring-batch-card-subtitle">
            {isReady
              ? "Tailored version ready. Upright's assessment is unchanged."
              : 'Preparing the tailored version. This takes about a minute.'}
          </div>
        </header>
      </section>
    );
  }

  return (
    <section className="tailoring-batch-card" aria-label={`Turn on own insights for ${companyName}`}>
      <header className="tailoring-batch-card-header">
        <div className="tailoring-batch-card-title">{`Turn on own insights for ${companyName}`}</div>
        <div className="tailoring-batch-card-subtitle">
          Creates a tailored version of the assessment that your team can adjust. Ready in about a minute.
        </div>
      </header>
      <div className="tailoring-batch-card-actions">
        <button
          type="button"
          className="tailoring-batch-card-action tailoring-batch-card-action--primary"
          disabled={startMutation.isPending}
          onClick={() => {
            startMutation.mutate();
          }}
        >
          {startMutation.isPending ? 'Turning on…' : 'Turn on'}
        </button>
        <button
          type="button"
          className="tailoring-batch-card-action tailoring-batch-card-action--secondary"
          disabled={startMutation.isPending}
          onClick={() => {
            setIsDeclined(true);
            continueWith(`Not now. Keep ${companyName} on Upright's assessment only.`);
          }}
        >
          Not now
        </button>
      </div>
      {startMutation.isError && (
        <div className="tailoring-batch-card-error" role="alert">
          Own insights could not be turned on. Try again, or contact Upright support if it keeps failing.
        </div>
      )}
    </section>
  );
}
